import { requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { px } from '@disy/lib/px';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ResizeInfo, WithResizeInfo } from 'cadenza/workbook/layout-management/resize-utils';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import { getMapViewLayoutSetting } from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import type { MapViewSidebar } from 'cadenza/workbook/view/map/sidebar/map-view-sidebar';
import type { View } from 'cadenza/workbook/view/view';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';

const DETECTION_RANGE_FOR_RESIZING = 4;

export function handleSidebarResizing(sidebar: MapViewSidebar & { dResize?: ResizeInfo }) {
  const { left, right } = sidebar.getBoundingClientRect();
  const resizeDirection = requireNonNullable(sidebar.dResize);

  startResizing();
  const eventKeys: Teardown[] = [
    on(window, 'pointermove', (event: PointerEvent) => {
      const { clientX: newX } = event;
      const newWidth = resizeDirection.right ? newX - left : right - newX;
      sidebar.parentElement?.style.setProperty('--map-legend-sidebar-width', px(newWidth));
    }),
    on(window, 'pointerup', () => {
      tearDown(eventKeys);
      // Set the actual target width in the end, which is clamped in CSS.
      sidebar.parentElement?.style.setProperty('--map-legend-sidebar-width', px(getWidth(sidebar)));
      persistSidebarWidth(sidebar);
      endResizing();
    }),
  ];

  function startResizing() {
    sidebar.dispatchEvent(new CustomEvent('d-map-view-sidebar-resizestart'));
    sidebar.classList.add('disable-user-selection');
  }

  function endResizing() {
    sidebar.dispatchEvent(new CustomEvent('d-map-view-sidebar-resizeend'));
    sidebar.classList.remove('disable-user-selection');
  }
}

function persistSidebarWidth(sidebar: MapViewSidebar) {
  getStore().dispatch(
    executeCommand(
      Command.CHANGE_VIEW_SETTINGS({
        viewId: sidebar.viewId,
        settings: {
          [MapViewSettingsKey.toPersistenceKey('SIDEBAR_WIDTH')]: String(getWidth(sidebar)),
        },
      }),
    ),
  );
}

function getWidth(sidebar: MapViewSidebar) {
  return sidebar.getBoundingClientRect().width;
}

export function getSidebarResizeDirection(event: PointerEvent, sidebar: MapViewSidebar) {
  const x = event.clientX;
  const rect = sidebar.getBoundingClientRect();
  const layout = getMapViewLayoutSetting(getStore().getState(), sidebar.viewId);

  switch (layout) {
    case 'SIDEBAR_LEFT':
      return {
        top: false,
        right: Math.abs(rect.right - x) <= DETECTION_RANGE_FOR_RESIZING,
        bottom: false,
        left: false,
      };
    case 'SIDEBAR_RIGHT':
      return {
        top: false,
        right: false,
        bottom: false,
        left: Math.abs(rect.left - x) <= DETECTION_RANGE_FOR_RESIZING,
      };
    default:
      throw new Error('Unsupported layout for sidebar resizing');
  }
}

export function isResizeDirectionAllowed(event: PointerEvent, sidebar: MapViewSidebar) {
  const layout = getMapViewLayoutSetting(getStore().getState(), sidebar.viewId);
  const resizeDirection = getSidebarResizeDirection(event, sidebar);
  return layout === 'SIDEBAR_LEFT' ? resizeDirection.right : resizeDirection.left;
}

export function isNearSidebarBorder(event: PointerEvent, view?: View) {
  const potentialSidebar = view?.querySelector<WithResizeInfo<MapViewSidebar>>('.d-map-view-sidebar');
  if (potentialSidebar) {
    const rect = potentialSidebar.getBoundingClientRect();
    if (
      rect.top <= event.clientY &&
      event.clientY <= rect.bottom &&
      isResizeDirectionAllowed(event, potentialSidebar)
    ) {
      return potentialSidebar;
    }
  }
}
